import { useCallback, useState } from "react";
import { authApi } from "../../../api";

export interface UseResetPasswordArgs {
    email: string;
    code: string;
    onSuccess: () => void;
}

export interface UseResetPasswordReturn {
    pwd: string;
    setPwd: (v: string) => void;
    confirm: string;
    setConfirm: (v: string) => void;

    loading: boolean;
    error: string | null;
    success: boolean;

    submit: () => Promise<void>;
}

export function useResetPassword(
    args: UseResetPasswordArgs,
): UseResetPasswordReturn {
    const { email, code, onSuccess } = args;
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const submit = useCallback(async () => {
        setError(null);

        if (!pwd || !confirm) {
            setError("Please fill in both fields.");
            return;
        }
        if (pwd !== confirm) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            // Reset the password on the backend
            await authApi.resetPasswordWithCode(email, code, pwd);
            setSuccess(true);

            // Small delay so user sees success, then redirect to login
            setTimeout(() => {
                onSuccess();
            }, 800);
        } catch (e: unknown) {
            const err = e as { response?: { data?: { detail?: string } }; message?: string };
            setError(
                err?.response?.data?.detail ?? err?.message ?? "Failed to reset password.",
            );
        } finally {
            setLoading(false);
        }
    }, [email, code, pwd, confirm, onSuccess]);

    return { pwd, setPwd, confirm, setConfirm, loading, error, success, submit };
}
